import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { parse } from "query-string";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjsGenerateConfig from "rc-picker/lib/generate/dayjs";
import generatePicker from "antd/es/date-picker/generatePicker";
import CalendarIconDates from "components/Icons/Calendars/CalendarIconDates";
import styles from "./dateRange.module.less";

dayjs.extend(customParseFormat);

const { RangePicker } = generatePicker(dayjsGenerateConfig);

const DateRangeNavigate = (props) => {
  const {
    label = "Date Range",
    noLabel = false,
    allowClear = false,
    beginKey = "from",
    endKey = "to",
    pickerStyle = {},
    pickerProps = {},
    noBorder = false,
    largeText = false,
    iconAsPrefix = false,
    futureDateDisabled = true,
    navigate,
    additionalNavigation,
    defaultTo,
    defaultFrom,
    onSelect,
  } = props;

  const location = useLocation();
  const search = parse(location.search);
  const queryStart = search[beginKey];
  const queryEnd = search[endKey];
  const dateFormat = "MM/DD/YY";
  const dateUrlFormat = "YYYY-MM-DD";
  const calendarIcon = <CalendarIconDates />;
  const parseDate = (dateStr) => {
    // check if dayjs generated from dateStr is (a) a valid date (b) is a past date.
    // return null if either false, return dayjs if a & b.
    const date = dayjs(dateStr, dateUrlFormat, true);
    return date.isValid() && date.isBefore(dayjs()) ? date : undefined;
  };

  const [from, setFrom] = useState(defaultFrom || null);
  const [to, setTo] = useState(defaultTo || null);

  const handleDatePickerChange = (dates, dateStrings) => {
    if (dates) {
      navigate({
        [beginKey]: dates[0].format(dateUrlFormat),
        [endKey]: dates[1].format(dateUrlFormat),
        ...additionalNavigation,
      });
      if (onSelect) {
        onSelect("from", dayjs(dateStrings[0]).format(dateUrlFormat));
        onSelect("to", dayjs(dateStrings[1]).format(dateUrlFormat));
      }
    } else {
      navigate({ [beginKey]: null, [endKey]: null });
    }
  };

  useEffect(() => {
    setFrom(parseDate(queryStart));
    setTo(parseDate(queryEnd));
  }, [queryStart, queryEnd]);

  const disabledDateToday = (current) => {
    if (futureDateDisabled) {
      return current && current > dayjs().endOf("day");
    }
    return false;
  };

  return (
    <div className={styles.dateRange}>
      {!noLabel && <div className={styles.label}>{label}</div>}

      <RangePicker
        allowClear={allowClear}
        className={[
          styles.rangePicker,
          ...(iconAsPrefix ? [styles.iconAtTheBegining, styles.activeBar] : [styles.standardViewPadding]),
          ...(noBorder ? [styles.noBorder] : []),
          ...(largeText ? [styles.largeText] : []),
        ].join(" ")}
        popupClassName={styles.calendar}
        onChange={handleDatePickerChange}
        value={[from, to]}
        format={dateFormat}
        separator="-"
        style={pickerStyle}
        disabledDate={disabledDateToday}
        suffixIcon={calendarIcon}
        ranges={{
          Today: [dayjs(), dayjs()],
          Yesterday: [dayjs().subtract(1, "days"), dayjs().subtract(1, "days")],
          "This Week": [dayjs().startOf("week"), dayjs()],
          "Last Week": [dayjs().subtract(1, "weeks").startOf("week"), dayjs().subtract(1, "weeks").endOf("week")],
          "This Month": [dayjs().startOf("month"), dayjs()],
          "Last Month": [dayjs().subtract(1, "months").startOf("month"), dayjs().subtract(1, "months").endOf("month")],
          "Year To Date": [dayjs().startOf("year"), dayjs()],
        }}
        {...pickerProps}
      />
    </div>
  );
};

DateRangeNavigate.propTypes = {
  navigate: PropTypes.func,
  onSelect: PropTypes.func,
  label: PropTypes.string,
  beginKey: PropTypes.string,
  endKey: PropTypes.string,
  defaultTo: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  defaultFrom: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  noLabel: PropTypes.bool,
  pickerStyle: PropTypes.objectOf(PropTypes.string),
  // pass thru props to antd RangePicker component
  pickerProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  noBorder: PropTypes.bool,
  largeText: PropTypes.bool,
  additionalNavigation: PropTypes.objectOf(PropTypes.string),
  futureDateDisabled: PropTypes.bool,
  iconAsPrefix: PropTypes.bool,
  allowClear: PropTypes.bool,
};

export default DateRangeNavigate;
